import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import axiosInstance from "../utils/axiosInstance";
import AddressAutocomplete from "./ui/AddressAutocomplete";

const AddProjectModal = ({ isOpen, onClose, onAddProject }) => {
  const [submissionError, setSubmissionError] = useState("");
  const [users, setUsers] = useState([]);
  const [workerRates, setWorkerRates] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState("");
  const [selectedWorkerRate, setSelectedWorkerRate] = useState("");

  useEffect(() => {
    // Prevent background scrolling when modal is open
    if (isOpen) {
      document.body.style.overflow = "hidden";
      // Fetch users for manager dropdown
      fetchUsers();
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto"; // Cleanup on component unmount
    };
  }, [isOpen]);

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get("/users");
      setUsers(response.data.items || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const handleAddWorkerRate = () => {
    if (!selectedWorker || !selectedWorkerRate) {
      alert("Please select a worker and enter a rate");
      return;
    }
    
    const rate = parseFloat(selectedWorkerRate);
    if (isNaN(rate) || rate <= 0) {
      alert("Please enter a valid rate");
      return;
    }
    
    // Check if worker already has a rate
    if (workerRates.some(wr => wr.workerId === selectedWorker)) {
      alert("This worker already has a rate. Please remove it first.");
      return;
    }
    
    setWorkerRates([...workerRates, {
      workerId: selectedWorker,
      hourlyRate: rate
    }]);
    setSelectedWorker("");
    setSelectedWorkerRate("");
  };

  const handleRemoveWorkerRate = (workerId) => {
    setWorkerRates(workerRates.filter(wr => wr.workerId !== workerId));
  };

  const getWorkerName = (workerId) => {
    const user = users.find(u => u._id === workerId);
    return user ? `${user.firstname} ${user.lastname}` : "Unknown";
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      address: "",
      zipCode: "",
      city: "",
      status: "upcoming",
      startDate: "",
      endDate: "",
      description: "",
      manager: "",
      billingModel: "",
      hourlyRate: "",
      fixedPrice: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Project name is required"),
      address: Yup.string().required("Address is required"),
      zipCode: Yup.string().required("Zip code is required"),
      city: Yup.string().required("City is required"),
      status: Yup.string()
        .oneOf(["upcoming", "in_progress", "done", "archived"])
        .required("Status is required"),
      startDate: Yup.date().nullable(),
      endDate: Yup.date().nullable(),
      description: Yup.string(),
      manager: Yup.string(),
    }),
    onSubmit: async (values) => {
      try {
        setSubmissionError(""); // Clear any existing error

        // Construct payload
        const projectPayload = {
          name: values.name.trim(),
          address: values.address.trim(),
          zipCode: values.zipCode.trim(),
          city: values.city.trim(),
          status: values.status,
          description: values.description.trim(),
        };

        // Add optional fields if provided
        if (values.startDate) {
          projectPayload.startDate = values.startDate;
        }
        if (values.endDate) {
          projectPayload.endDate = values.endDate;
        }
        if (values.manager) {
          projectPayload.manager = values.manager;
        }

        // Add billing configuration
        if (values.billingModel) {
          projectPayload.billingModel = values.billingModel;
        }
        if (values.hourlyRate) {
          projectPayload.hourlyRate = parseFloat(values.hourlyRate);
        }
        if (values.fixedPrice) {
          projectPayload.fixedPrice = parseFloat(values.fixedPrice);
        }
        if (workerRates.length > 0) {
          projectPayload.workerRates = workerRates;
        }

        // Send a POST request to create a new project
        const response = await axiosInstance.post("/projects/create", projectPayload);

        // Handle successful creation
        console.log("Project created:", response.data);
        formik.resetForm();
        setWorkerRates([]); // Clear worker rates
        setSelectedWorker("");
        setSelectedWorkerRate("");
        onClose(); // Close the modal
        if (onAddProject) {
          onAddProject(); // Trigger refresh of projects list
        }
      } catch (error) {
        // Handle errors
        console.error("Error creating project:", error);
        setSubmissionError(
          error.response?.data?.message ||
            "An error occurred while creating the project."
        );
      }
    },
  });

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      {/* Modal Content */}
      <div className="bg-white rounded-lg shadow-lg w-full max-w-lg max-h-screen overflow-y-auto p-6">
        <h2 className="text-xl font-bold mb-4">Add Project</h2>
        <form onSubmit={formik.handleSubmit} className="space-y-4">
          {/* Project Name */}
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-700"
            >
              Project Name *
            </label>
            <input
              id="name"
              name="name"
              type="text"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="w-full border rounded px-3 py-2"
            />
            {formik.touched.name && formik.errors.name && (
              <div className="text-red-500 text-sm">{formik.errors.name}</div>
            )}
          </div>

          {/* Address */}
          <div>
            <label
              htmlFor="address"
              className="block text-sm font-medium text-gray-700"
            >
              Address *
            </label>
            <AddressAutocomplete
              id="address"
              name="address"
              value={formik.values.address}
              onChange={(address, zipCode, city) => {
                // Update all fields at once when address is selected from autocomplete
                // zipCode and city will be empty strings if not provided, which is fine
                formik.setValues({
                  ...formik.values,
                  address: address || "",
                  zipCode: zipCode !== undefined ? zipCode : formik.values.zipCode,
                  city: city !== undefined ? city : formik.values.city,
                });
              }}
              onBlur={formik.handleBlur}
              className="w-full border rounded px-3 py-2"
              required
            />
            {formik.touched.address && formik.errors.address && (
              <div className="text-red-500 text-sm">
                {formik.errors.address}
              </div>
            )}
          </div>

          {/* Zip Code */}
          <div>
            <label
              htmlFor="zipCode"
              className="block text-sm font-medium text-gray-700"
            >
              Zip Code *
            </label>
            <input
              id="zipCode"
              name="zipCode"
              type="text"
              value={formik.values.zipCode}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="w-full border rounded px-3 py-2"
            />
            {formik.touched.zipCode && formik.errors.zipCode && (
              <div className="text-red-500 text-sm">
                {formik.errors.zipCode}
              </div>
            )}
          </div>

          {/* City */}
          <div>
            <label
              htmlFor="city"
              className="block text-sm font-medium text-gray-700"
            >
              City *
            </label>
            <input
              id="city"
              name="city"
              type="text"
              value={formik.values.city}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="w-full border rounded px-3 py-2"
            />
            {formik.touched.city && formik.errors.city && (
              <div className="text-red-500 text-sm">{formik.errors.city}</div>
            )}
          </div>

          {/* Status */}
          <div>
            <label
              htmlFor="status"
              className="block text-sm font-medium text-gray-700"
            >
              Status *
            </label>
            <select
              id="status"
              name="status"
              value={formik.values.status}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="w-full border rounded px-3 py-2"
            >
              <option value="upcoming">Upcoming</option>
              <option value="in_progress">In Progress</option>
              <option value="done">Done</option>
              <option value="archived">Archived</option>
            </select>
            {formik.touched.status && formik.errors.status && (
              <div className="text-red-500 text-sm">{formik.errors.status}</div>
            )}
          </div>

          {/* Start Date */}
          <div>
            <label
              htmlFor="startDate"
              className="block text-sm font-medium text-gray-700"
            >
              Start Date
            </label>
            <input
              id="startDate"
              name="startDate"
              type="date"
              value={formik.values.startDate}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="w-full border rounded px-3 py-2"
            />
          </div>

          {/* End Date */}
          <div>
            <label
              htmlFor="endDate"
              className="block text-sm font-medium text-gray-700"
            >
              End Date
            </label>
            <input
              id="endDate"
              name="endDate"
              type="date"
              value={formik.values.endDate}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="w-full border rounded px-3 py-2"
            />
          </div>

          {/* Manager */}
          <div>
            <label
              htmlFor="manager"
              className="block text-sm font-medium text-gray-700"
            >
              Manager
            </label>
            <select
              id="manager"
              name="manager"
              value={formik.values.manager}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="w-full border rounded px-3 py-2"
            >
              <option value="">Select a manager (optional)</option>
              {users.map((user) => (
                <option key={user._id} value={user._id}>
                  {user.firstname} {user.lastname}
                </option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label
              htmlFor="description"
              className="block text-sm font-medium text-gray-700"
            >
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              rows="4"
              className="w-full border rounded px-3 py-2"
            />
          </div>

          {/* Billing Configuration Section */}
          <div className="border-t pt-4 mt-4">
            <h3 className="text-lg font-semibold mb-3">Billing Configuration</h3>
            
            {/* Billing Model */}
            <div className="mb-4">
              <label
                htmlFor="billingModel"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Billing Model
              </label>
              <select
                id="billingModel"
                name="billingModel"
                value={formik.values.billingModel}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="w-full border rounded px-3 py-2"
              >
                <option value="">Not set</option>
                <option value="hourly">Hourly/Ongoing</option>
                <option value="fixed_price">Fixed Price</option>
              </select>
            </div>

            {/* Conditional Hourly Rate */}
            {formik.values.billingModel === "hourly" && (
              <div className="mb-4">
                <label
                  htmlFor="hourlyRate"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Default Hourly Rate (kr/hr)
                </label>
                <input
                  id="hourlyRate"
                  name="hourlyRate"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formik.values.hourlyRate}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="e.g., 850"
                  className="w-full border rounded px-3 py-2"
                />
              </div>
            )}

            {/* Conditional Fixed Price */}
            {formik.values.billingModel === "fixed_price" && (
              <div className="mb-4">
                <label
                  htmlFor="fixedPrice"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Total Budget/Fixed Price (kr)
                </label>
                <input
                  id="fixedPrice"
                  name="fixedPrice"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formik.values.fixedPrice}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="e.g., 150000"
                  className="w-full border rounded px-3 py-2"
                />
              </div>
            )}

            {/* Worker Hourly Rates */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Worker Hourly Rates
              </label>
              <p className="text-xs text-gray-600 mb-3">
                Used for hourly billing & cost tracking
              </p>
              
              {/* Add Worker Rate UI */}
              <div className="flex gap-2 mb-3">
                <select
                  value={selectedWorker}
                  onChange={(e) => setSelectedWorker(e.target.value)}
                  className="flex-1 border rounded px-3 py-2 text-sm"
                >
                  <option value="">Select Worker</option>
                  {users
                    .filter(u => !workerRates.some(wr => wr.workerId === u._id))
                    .map((user) => (
                      <option key={user._id} value={user._id}>
                        {user.firstname} {user.lastname}
                      </option>
                    ))}
                </select>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={selectedWorkerRate}
                  onChange={(e) => setSelectedWorkerRate(e.target.value)}
                  placeholder="Rate"
                  className="w-24 border rounded px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  onClick={handleAddWorkerRate}
                  className="px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                >
                  Add
                </button>
              </div>

              {/* Worker Rates List */}
              {workerRates.length > 0 && (
                <div className="space-y-2">
                  {workerRates.map((wr) => (
                    <div
                      key={wr.workerId}
                      className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded text-sm"
                    >
                      <span>
                        {getWorkerName(wr.workerId)}: {wr.hourlyRate} kr/hr
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveWorkerRate(wr.workerId)}
                        className="text-red-600 hover:text-red-800 font-medium"
                      >
                        ✕ Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Display submission error message */}
          {submissionError && (
            <div className="text-red-500 mb-4">{submissionError}</div>
          )}

          {/* Buttons */}
          <div className="flex justify-end space-x-2 mt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 rounded"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded"
            >
              Add Project
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default AddProjectModal;
